/**
 * The starter menu a newly registered hotel gets, and the two small rules that
 * govern a menu item's price.
 *
 * A kitchen that starts completely empty is a kitchen nobody finishes setting
 * up: the owner signs in, sees nothing, and has to invent a category structure
 * before they can ring up a single bill. So registration asks one question -
 * what do you serve - and lays out a plausible Indian hotel menu to match,
 * already routed to the right kitchen station.
 *
 * Everything arrives priced at zero and switched off. That is deliberate: the
 * defaults are a starting point, not a price list, and nothing can reach a bill
 * until somebody has said what it costs.
 */
import { MENU_SECTIONS } from './menu-sections.js';
export const FOOD_TYPES = ['VEG', 'NONVEG', 'BOTH'];
export const FOOD_TYPE_LABELS = {
    VEG: 'Vegetarian',
    NONVEG: 'Non-vegetarian',
    BOTH: 'Both',
};
/**
 * The sections a new hotel starts with, in menu-card order.
 *
 * Taken from MENU_SECTIONS rather than listed again here, so the starter menu,
 * the till, the guest card and the reports cannot drift into disagreeing about
 * what a section is called or where it sits.
 */
export const DEFAULT_CATEGORIES = MENU_SECTIONS.filter((section) => ['Breakfast', 'Soups', 'Starters', 'Main Course', 'Breads', 'Rice & Biryani', 'Accompaniments', 'Desserts', 'Beverages'].includes(section.name)).map((section) => ({ name: section.name, sortOrder: section.sortOrder }));
const meat = (key, name, unit, unitCost, reorderLevel, parLevel, shelfLifeDays) => ({
    key,
    name,
    unit,
    category: 'Butchery',
    unitCost,
    reorderLevel,
    parLevel,
    gstRate: 5,
    shelfLifeDays,
    isVegetarian: false,
});
const store = (key, name, unit, category, unitCost, reorderLevel, parLevel, shelfLifeDays, gstRate = 5) => ({
    key,
    name,
    unit,
    category,
    unitCost,
    reorderLevel,
    parLevel,
    gstRate,
    shelfLifeDays,
    isVegetarian: true,
});
/**
 * The store cupboard behind the starter menu.
 *
 * Costs are indicative wholesale rates in rupees per unit — close enough that a
 * plate cost means something on day one, and the first goods receipt re-weights
 * them to what the hotel actually pays.
 */
export const DEFAULT_INGREDIENTS = [
    /* Butchery */
    meat('CHICKEN_CURRY', 'Chicken (Curry Cut)', 'KG', 260, 5, 20, 2),
    meat('CHICKEN_BONELESS', 'Chicken (Boneless)', 'KG', 320, 4, 16, 2),
    meat('MUTTON_CURRY', 'Mutton (Curry Cut)', 'KG', 720, 3, 12, 2),
    meat('MUTTON_KEEMA', 'Mutton Keema', 'KG', 680, 2, 8, 2),
    meat('FISH_FILLET', 'Fish Fillet', 'KG', 420, 3, 12, 2),
    meat('EGG', 'Eggs', 'PIECE', 7, 60, 240, 14),
    /* Dairy */
    store('MILK', 'Milk', 'LITRE', 'Dairy', 62, 20, 80, 3),
    store('CURD', 'Curd', 'KG', 'Dairy', 70, 8, 30, 4),
    store('PANEER', 'Paneer', 'KG', 'Dairy', 380, 3, 12, 4),
    store('BUTTER', 'Butter', 'KG', 'Dairy', 520, 3, 12, 60),
    store('CREAM', 'Fresh Cream', 'LITRE', 'Dairy', 220, 2, 8, 15),
    store('GHEE', 'Ghee', 'KG', 'Dairy', 640, 2, 8, 180),
    /* Produce */
    store('ONION', 'Onion', 'KG', 'Produce', 38, 15, 60, 20),
    store('TOMATO', 'Tomato', 'KG', 'Produce', 42, 10, 40, 7),
    store('POTATO', 'Potato', 'KG', 'Produce', 32, 12, 50, 25),
    store('GINGER', 'Ginger', 'KG', 'Produce', 120, 2, 8, 15),
    store('GARLIC', 'Garlic', 'KG', 'Produce', 140, 2, 8, 30),
    store('GREEN_CHILLI', 'Green Chilli', 'KG', 'Produce', 80, 1, 5, 7),
    store('CORIANDER', 'Coriander Leaves', 'KG', 'Produce', 90, 1, 5, 4),
    store('MINT', 'Mint Leaves', 'KG', 'Produce', 110, 1, 4, 4),
    store('LEMON', 'Lemon', 'PIECE', 'Produce', 6, 30, 120, 12),
    store('CUCUMBER', 'Cucumber', 'KG', 'Produce', 40, 3, 12, 7),
    store('CARROT', 'Carrot', 'KG', 'Produce', 45, 3, 12, 12),
    store('CABBAGE', 'Cabbage', 'KG', 'Produce', 30, 3, 12, 10),
    store('CAPSICUM', 'Capsicum', 'KG', 'Produce', 70, 3, 12, 8),
    store('CAULIFLOWER', 'Cauliflower', 'KG', 'Produce', 45, 3, 12, 7),
    store('SPINACH', 'Spinach', 'KG', 'Produce', 40, 2, 8, 3),
    store('GREEN_PEAS', 'Green Peas', 'KG', 'Produce', 90, 3, 12, 30),
    store('SWEET_CORN', 'Sweet Corn', 'KG', 'Produce', 95, 2, 8, 30),
    store('MANGO_PULP', 'Mango Pulp', 'KG', 'Produce', 180, 2, 8, 90),
    store('MIXED_FRUIT', 'Mixed Fruit', 'KG', 'Produce', 120, 2, 8, 4),
    /* Grocery */
    store('BASMATI', 'Basmati Rice', 'KG', 'Grocery', 110, 10, 40, 180),
    store('RAW_RICE', 'Raw Rice', 'KG', 'Grocery', 55, 20, 80, 180),
    store('ATTA', 'Wheat Flour', 'KG', 'Grocery', 45, 10, 40, 90),
    store('MAIDA', 'Refined Flour', 'KG', 'Grocery', 48, 8, 30, 90),
    store('RAVA', 'Semolina', 'KG', 'Grocery', 52, 5, 20, 90),
    store('TOOR_DAL', 'Toor Dal', 'KG', 'Grocery', 130, 6, 24, 180),
    store('URAD_DAL', 'Urad Dal', 'KG', 'Grocery', 140, 5, 20, 180),
    store('CHANA', 'Chickpeas', 'KG', 'Grocery', 95, 5, 20, 180),
    store('SUGAR', 'Sugar', 'KG', 'Grocery', 45, 10, 40, 365),
    store('SALT', 'Salt', 'KG', 'Grocery', 22, 5, 20, 365),
    store('OIL', 'Refined Oil', 'LITRE', 'Grocery', 145, 10, 40, 180),
    store('TEA', 'Tea Leaves', 'KG', 'Grocery', 380, 2, 8, 180),
    store('COFFEE', 'Coffee Powder', 'KG', 'Grocery', 520, 2, 8, 120),
    store('CASHEW', 'Cashew Nuts', 'KG', 'Grocery', 850, 1, 5, 120),
    /* Spices */
    store('GARAM_MASALA', 'Garam Masala', 'KG', 'Spices', 420, 1, 4, 180),
    store('CHILLI_POWDER', 'Red Chilli Powder', 'KG', 'Spices', 280, 2, 8, 180),
    store('TURMERIC', 'Turmeric Powder', 'KG', 'Spices', 240, 1, 4, 180),
    store('CUMIN', 'Cumin Seeds', 'KG', 'Spices', 380, 1, 4, 180),
    store('CORIANDER_POWDER', 'Coriander Powder', 'KG', 'Spices', 260, 1, 4, 180),
    store('SAMBAR_POWDER', 'Sambar Powder', 'KG', 'Spices', 300, 1, 4, 120),
    /* Bought in ready to serve */
    store('PAPAD', 'Papad', 'PIECE', 'Packaged', 4, 50, 200, 120),
    store('BOONDI', 'Boondi', 'KG', 'Packaged', 180, 1, 5, 45),
    store('GULAB_JAMUN_MIX', 'Gulab Jamun Mix', 'KG', 'Packaged', 260, 1, 5, 120),
    store('ICE_CREAM', 'Vanilla Ice Cream', 'LITRE', 'Packaged', 180, 3, 12, 90),
    store('RASMALAI', 'Rasmalai', 'PIECE', 'Packaged', 22, 20, 80, 5),
    store('CUSTARD_POWDER', 'Custard Powder', 'KG', 'Packaged', 220, 1, 4, 180),
    store('SPRING_ROLL_SHEET', 'Spring Roll Sheets', 'PIECE', 'Packaged', 4, 40, 160, 30),
    store('SODA', 'Soda Water', 'PIECE', 'Packaged', 15, 24, 96, 120, 18),
    store('WATER_BOTTLE', 'Packaged Water 1L', 'PIECE', 'Packaged', 12, 48, 200, 180, 18),
];
const veg = (name, category, station, recipe, staple = false) => ({ name, category, station, isVegetarian: true, recipe, staple });
const nonVeg = (name, category, station, recipe) => ({ name, category, station, isVegetarian: false, recipe });
export const DEFAULT_MENU_ITEMS = [
    /* Beverages */
    veg('Masala Chai', 'Beverages', 'BEVERAGE', [
        ['MILK', 0.12],
        ['TEA', 0.005],
        ['SUGAR', 0.012],
        ['GINGER', 0.002],
    ], true),
    veg('Filter Coffee', 'Beverages', 'BEVERAGE', [
        ['MILK', 0.14],
        ['COFFEE', 0.012],
        ['SUGAR', 0.012],
    ], true),
    veg('Bottled Water', 'Beverages', 'BEVERAGE', [['WATER_BOTTLE', 1]], true),
    veg('Fresh Lime Soda', 'Beverages', 'BEVERAGE', [
        ['SODA', 1],
        ['LEMON', 1],
        ['SUGAR', 0.015],
        ['SALT', 0.001],
    ], true),
    veg('Buttermilk', 'Beverages', 'BEVERAGE', [
        ['CURD', 0.12],
        ['SALT', 0.001],
        ['CUMIN', 0.001],
        ['CORIANDER', 0.002],
    ], true),
    veg('Cold Coffee', 'Beverages', 'BEVERAGE', [
        ['MILK', 0.2],
        ['COFFEE', 0.01],
        ['SUGAR', 0.02],
        ['ICE_CREAM', 0.03],
    ]),
    veg('Mango Lassi', 'Beverages', 'BEVERAGE', [
        ['CURD', 0.15],
        ['MANGO_PULP', 0.08],
        ['SUGAR', 0.02],
        ['MILK', 0.05],
    ]),
    /* Breakfast */
    veg('Idli Sambar', 'Breakfast', 'KITCHEN', [
        ['RAW_RICE', 0.09],
        ['URAD_DAL', 0.03],
        ['TOOR_DAL', 0.04],
        ['SAMBAR_POWDER', 0.008],
        ['ONION', 0.03],
        ['TOMATO', 0.03],
    ], true),
    veg('Masala Dosa', 'Breakfast', 'KITCHEN', [
        ['RAW_RICE', 0.1],
        ['URAD_DAL', 0.035],
        ['POTATO', 0.12],
        ['ONION', 0.04],
        ['TURMERIC', 0.001],
        ['OIL', 0.02],
    ]),
    veg('Poori Bhaji', 'Breakfast', 'KITCHEN', [
        ['ATTA', 0.1],
        ['POTATO', 0.15],
        ['ONION', 0.04],
        ['OIL', 0.04],
        ['TURMERIC', 0.001],
        ['GREEN_CHILLI', 0.005],
    ]),
    veg('Upma', 'Breakfast', 'KITCHEN', [
        ['RAVA', 0.09],
        ['ONION', 0.04],
        ['GREEN_CHILLI', 0.005],
        ['GHEE', 0.01],
        ['CORIANDER', 0.004],
    ]),
    veg('Aloo Paratha', 'Breakfast', 'KITCHEN', [
        ['ATTA', 0.11],
        ['POTATO', 0.14],
        ['ONION', 0.03],
        ['GHEE', 0.015],
        ['CHILLI_POWDER', 0.002],
        ['CURD', 0.05],
    ]),
    nonVeg('Bread Omelette', 'Breakfast', 'KITCHEN', [
        ['EGG', 2],
        ['MAIDA', 0.06],
        ['ONION', 0.03],
        ['GREEN_CHILLI', 0.004],
        ['OIL', 0.012],
        ['BUTTER', 0.008],
    ]),
    nonVeg('Egg Bhurji', 'Breakfast', 'KITCHEN', [
        ['EGG', 3],
        ['ONION', 0.05],
        ['TOMATO', 0.04],
        ['GREEN_CHILLI', 0.005],
        ['OIL', 0.012],
        ['CORIANDER', 0.003],
    ]),
    /* Soups */
    veg('Tomato Shorba', 'Soups', 'KITCHEN', [
        ['TOMATO', 0.18],
        ['ONION', 0.03],
        ['GARLIC', 0.005],
        ['BUTTER', 0.008],
        ['CREAM', 0.015],
    ]),
    veg('Sweet Corn Soup', 'Soups', 'KITCHEN', [
        ['SWEET_CORN', 0.09],
        ['CARROT', 0.03],
        ['CABBAGE', 0.03],
        ['MAIDA', 0.008],
        ['SALT', 0.002],
    ]),
    nonVeg('Chicken Clear Soup', 'Soups', 'KITCHEN', [
        ['CHICKEN_BONELESS', 0.06],
        ['CARROT', 0.03],
        ['CABBAGE', 0.03],
        ['GARLIC', 0.004],
        ['CORIANDER', 0.003],
    ]),
    /* Starters */
    veg('Veg Spring Roll', 'Starters', 'FRYER', [
        ['SPRING_ROLL_SHEET', 4],
        ['CABBAGE', 0.06],
        ['CARROT', 0.04],
        ['CAPSICUM', 0.03],
        ['OIL', 0.04],
        ['GARLIC', 0.004],
    ]),
    veg('Paneer Tikka', 'Starters', 'GRILL', [
        ['PANEER', 0.16],
        ['CURD', 0.06],
        ['CAPSICUM', 0.05],
        ['ONION', 0.05],
        ['GARAM_MASALA', 0.004],
        ['CHILLI_POWDER', 0.003],
    ]),
    veg('Gobi Manchurian', 'Starters', 'FRYER', [
        ['CAULIFLOWER', 0.2],
        ['MAIDA', 0.05],
        ['CAPSICUM', 0.04],
        ['GARLIC', 0.006],
        ['OIL', 0.05],
        ['GREEN_CHILLI', 0.005],
    ]),
    nonVeg('Chicken 65', 'Starters', 'FRYER', [
        ['CHICKEN_BONELESS', 0.18],
        ['CURD', 0.04],
        ['MAIDA', 0.03],
        ['CHILLI_POWDER', 0.005],
        ['OIL', 0.05],
        ['GINGER', 0.005],
    ]),
    nonVeg('Tandoori Chicken', 'Starters', 'GRILL', [
        ['CHICKEN_CURRY', 0.4],
        ['CURD', 0.08],
        ['GARAM_MASALA', 0.005],
        ['CHILLI_POWDER', 0.005],
        ['GINGER', 0.006],
        ['GARLIC', 0.006],
    ]),
    nonVeg('Fish Amritsari', 'Starters', 'FRYER', [
        ['FISH_FILLET', 0.18],
        ['MAIDA', 0.035],
        ['CHILLI_POWDER', 0.004],
        ['GINGER', 0.005],
        ['OIL', 0.05],
        ['LEMON', 1],
    ]),
    nonVeg('Mutton Seekh Kebab', 'Starters', 'GRILL', [
        ['MUTTON_KEEMA', 0.18],
        ['ONION', 0.05],
        ['GARAM_MASALA', 0.005],
        ['GINGER', 0.005],
        ['GARLIC', 0.005],
        ['CORIANDER', 0.004],
    ]),
    /* Main course */
    veg('Dal Tadka', 'Main Course', 'KITCHEN', [
        ['TOOR_DAL', 0.09],
        ['ONION', 0.04],
        ['TOMATO', 0.04],
        ['GHEE', 0.012],
        ['CUMIN', 0.002],
        ['TURMERIC', 0.001],
    ], true),
    veg('Paneer Butter Masala', 'Main Course', 'KITCHEN', [
        ['PANEER', 0.15],
        ['TOMATO', 0.12],
        ['BUTTER', 0.02],
        ['CREAM', 0.03],
        ['CASHEW', 0.015],
        ['GARAM_MASALA', 0.004],
    ]),
    veg('Palak Paneer', 'Main Course', 'KITCHEN', [
        ['PANEER', 0.13],
        ['SPINACH', 0.2],
        ['ONION', 0.04],
        ['GARLIC', 0.005],
        ['CREAM', 0.02],
        ['CUMIN', 0.002],
    ]),
    veg('Mixed Veg Curry', 'Main Course', 'KITCHEN', [
        ['CARROT', 0.06],
        ['GREEN_PEAS', 0.05],
        ['CAULIFLOWER', 0.06],
        ['POTATO', 0.06],
        ['TOMATO', 0.06],
        ['OIL', 0.015],
    ]),
    veg('Chana Masala', 'Main Course', 'KITCHEN', [
        ['CHANA', 0.1],
        ['ONION', 0.05],
        ['TOMATO', 0.06],
        ['GARAM_MASALA', 0.004],
        ['CHILLI_POWDER', 0.003],
        ['OIL', 0.015],
    ]),
    nonVeg('Butter Chicken', 'Main Course', 'KITCHEN', [
        ['CHICKEN_BONELESS', 0.2],
        ['TOMATO', 0.12],
        ['BUTTER', 0.025],
        ['CREAM', 0.03],
        ['CASHEW', 0.015],
        ['GARAM_MASALA', 0.004],
    ]),
    nonVeg('Chicken Chettinad', 'Main Course', 'KITCHEN', [
        ['CHICKEN_CURRY', 0.25],
        ['ONION', 0.06],
        ['TOMATO', 0.05],
        ['CORIANDER_POWDER', 0.005],
        ['CHILLI_POWDER', 0.005],
        ['OIL', 0.02],
    ]),
    nonVeg('Mutton Rogan Josh', 'Main Course', 'KITCHEN', [
        ['MUTTON_CURRY', 0.22],
        ['CURD', 0.06],
        ['ONION', 0.06],
        ['GARAM_MASALA', 0.005],
        ['CHILLI_POWDER', 0.005],
        ['GHEE', 0.02],
    ]),
    nonVeg('Fish Curry', 'Main Course', 'KITCHEN', [
        ['FISH_FILLET', 0.2],
        ['TOMATO', 0.08],
        ['ONION', 0.05],
        ['TURMERIC', 0.002],
        ['CHILLI_POWDER', 0.004],
        ['OIL', 0.02],
    ]),
    nonVeg('Egg Curry', 'Main Course', 'KITCHEN', [
        ['EGG', 2],
        ['ONION', 0.06],
        ['TOMATO', 0.06],
        ['GARAM_MASALA', 0.003],
        ['OIL', 0.015],
        ['CORIANDER', 0.003],
    ]),
    /* Breads */
    veg('Tandoori Roti', 'Breads', 'OVEN', [
        ['ATTA', 0.06],
        ['SALT', 0.001],
        ['OIL', 0.003],
    ], true),
    veg('Butter Naan', 'Breads', 'OVEN', [
        ['MAIDA', 0.07],
        ['CURD', 0.02],
        ['BUTTER', 0.01],
        ['SALT', 0.001],
    ], true),
    veg('Garlic Naan', 'Breads', 'OVEN', [
        ['MAIDA', 0.07],
        ['CURD', 0.02],
        ['GARLIC', 0.006],
        ['BUTTER', 0.01],
        ['CORIANDER', 0.002],
    ]),
    veg('Laccha Paratha', 'Breads', 'OVEN', [
        ['ATTA', 0.08],
        ['GHEE', 0.015],
        ['SALT', 0.001],
    ]),
    /* Rice and biryani */
    veg('Steamed Rice', 'Rice & Biryani', 'KITCHEN', [
        ['RAW_RICE', 0.15],
        ['SALT', 0.001],
        ['OIL', 0.003],
    ], true),
    veg('Jeera Rice', 'Rice & Biryani', 'KITCHEN', [
        ['BASMATI', 0.14],
        ['CUMIN', 0.003],
        ['GHEE', 0.012],
        ['CORIANDER', 0.002],
    ], true),
    veg('Curd Rice', 'Rice & Biryani', 'KITCHEN', [
        ['RAW_RICE', 0.13],
        ['CURD', 0.12],
        ['GREEN_CHILLI', 0.003],
        ['GINGER', 0.003],
        ['CORIANDER', 0.002],
    ], true),
    veg('Veg Biryani', 'Rice & Biryani', 'KITCHEN', [
        ['BASMATI', 0.16],
        ['CARROT', 0.05],
        ['GREEN_PEAS', 0.04],
        ['CAULIFLOWER', 0.05],
        ['CURD', 0.05],
        ['GARAM_MASALA', 0.005],
        ['GHEE', 0.02],
    ]),
    nonVeg('Chicken Biryani', 'Rice & Biryani', 'KITCHEN', [
        ['BASMATI', 0.17],
        ['CHICKEN_CURRY', 0.2],
        ['CURD', 0.06],
        ['ONION', 0.06],
        ['GARAM_MASALA', 0.005],
        ['GHEE', 0.02],
        ['MINT', 0.004],
    ]),
    nonVeg('Mutton Biryani', 'Rice & Biryani', 'KITCHEN', [
        ['BASMATI', 0.17],
        ['MUTTON_CURRY', 0.2],
        ['CURD', 0.06],
        ['ONION', 0.06],
        ['GARAM_MASALA', 0.005],
        ['GHEE', 0.025],
        ['MINT', 0.004],
    ]),
    /* Accompaniments */
    veg('Green Salad', 'Accompaniments', 'KITCHEN', [
        ['CUCUMBER', 0.08],
        ['TOMATO', 0.06],
        ['ONION', 0.04],
        ['CARROT', 0.04],
        ['LEMON', 1],
    ], true),
    veg('Boondi Raita', 'Accompaniments', 'KITCHEN', [
        ['CURD', 0.15],
        ['BOONDI', 0.03],
        ['CUMIN', 0.001],
        ['SALT', 0.001],
    ], true),
    veg('Roasted Papad', 'Accompaniments', 'KITCHEN', [
        ['PAPAD', 2],
        ['OIL', 0.002],
    ], true),
    /* Desserts */
    veg('Gulab Jamun', 'Desserts', 'BAKERY', [
        ['GULAB_JAMUN_MIX', 0.05],
        ['SUGAR', 0.06],
        ['OIL', 0.02],
        ['GHEE', 0.005],
    ], true),
    veg('Vanilla Ice Cream', 'Desserts', 'BAKERY', [
        ['ICE_CREAM', 0.1],
        ['CASHEW', 0.004],
    ], true),
    veg('Rasmalai', 'Desserts', 'BAKERY', [
        ['RASMALAI', 2],
        ['MILK', 0.08],
        ['SUGAR', 0.015],
        ['CASHEW', 0.004],
    ]),
    veg('Fruit Custard', 'Desserts', 'BAKERY', [
        ['MILK', 0.15],
        ['CUSTARD_POWDER', 0.012],
        ['SUGAR', 0.02],
        ['MIXED_FRUIT', 0.08],
    ]),
];
/**
 * The starter categories and items for a hotel serving `foodType`.
 *
 * Categories come back in menu order and only if something lands in them, so a
 * vegetarian hotel does not open the app to an empty Starters tab.
 */
export function defaultMenuFor(foodType) {
    const items = DEFAULT_MENU_ITEMS.filter((item) => {
        if (foodType === 'BOTH')
            return true;
        if (foodType === 'VEG')
            return item.isVegetarian;
        return !item.isVegetarian || Boolean(item.staple);
    });
    const usedCategories = new Set(items.map((item) => item.category));
    const categories = DEFAULT_CATEGORIES.filter((category) => usedCategories.has(category.name)).map((category) => ({ ...category }));
    // Only what this menu actually cooks with: a vegetarian kitchen has no
    // business being handed a butchery to stocktake.
    const usedIngredients = new Set(items.flatMap((item) => item.recipe.map(([key]) => key)));
    const ingredients = DEFAULT_INGREDIENTS.filter((ingredient) => usedIngredients.has(ingredient.key)).map((ingredient) => ({ ...ingredient }));
    return { categories, items: [...items], ingredients };
}
/**
 * Whether an item may go on sale.
 *
 * The rule is the whole point of the zero default: an item with no price would
 * otherwise be ringable at nothing, and the first anybody knows of it is a
 * bill that does not add up.
 */
export const canActivateMenuItem = (price) => price > 0;
/**
 * The money taken off one bill line by the item's own discount percentage.
 *
 * Paise in, paise out - the caller has already converted, and the result is
 * clamped so a mis-typed percentage can never make a line negative.
 */
export function itemDiscountPaise(unitPricePaise, quantity, discountPct) {
    const gross = Math.round(unitPricePaise * quantity);
    const pct = Math.min(100, Math.max(0, discountPct));
    return Math.min(gross, Math.round((gross * pct) / 100));
}
/**
 * The address a table's QR code carries.
 *
 * Built from whatever origin the hotel already reaches Kortex on, so a code
 * printed from a deployed server works on a guest's phone and one printed from
 * a laptop running the app locally does not. The token identifies the hotel;
 * the table code is a hint, so the menu can greet the right table.
 */
export function guestMenuUrl(origin, token, tableCode) {
    const base = `${origin.replace(/\/+$/, '')}/m/${token}`;
    const code = tableCode?.trim();
    return code ? `${base}?table=${encodeURIComponent(code)}` : base;
}
/**
 * The address for booking a room, off the same token as the menu.
 *
 * One token per property covers both, so a hotel has a single thing to print
 * and a guest reading the menu is one tap from a room.
 */
export function roomBookingUrl(origin, token) {
    return `${origin.replace(/\/+$/, '')}/stay/${token}`;
}
//# sourceMappingURL=menu-defaults.js.map